import { remove } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type ReviewsViewContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { type CommentReactionSource, Reaction } from "@src/appV2/redesign/Workplace/Review/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import {
  getCommentsReactionUrl,
  type GetCommentsReactionUrlParams,
} from "./usePostCommentReaction";
import { workplaceCommentSchema } from "./useWorkplaceComments";

const deleteCommentReactionResponseSchema = z.object({
  data: workplaceCommentSchema,
});

export type DeleteCommentReactionResponse = z.infer<typeof deleteCommentReactionResponseSchema>;

interface UseDeleteCommentReactionParams extends GetCommentsReactionUrlParams {
  source: CommentReactionSource;
  viewContext: ReviewsViewContext;
}

interface DeleteCommentReactionVariables {
  reactionType: Reaction;
}

export function useDeleteCommentReaction(
  params: UseDeleteCommentReactionParams,
  options: UseMutationOptions<
    DeleteCommentReactionResponse,
    AxiosError,
    DeleteCommentReactionVariables
  > = {}
): UseMutationResult<DeleteCommentReactionResponse, AxiosError, DeleteCommentReactionVariables> {
  const { source, viewContext, ...urlParams } = params;

  return useMutation({
    mutationFn: async () => {
      const response = await remove({
        url: getCommentsReactionUrl(urlParams),
        responseSchema: deleteCommentReactionResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.DELETE_COMMENT_REACTION,
    },
    ...options,
    onSuccess: (data, variables, context) => {
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_COMMENT_REACTION_REMOVED, {
        workplaceId: urlParams.workplaceId,
        commentId: urlParams.commentId,
        reactionType: variables.reactionType,
        source,
        viewContext,
      });
      options.onSuccess?.(data, variables, context);
    },
  });
}
